/**
 * What the airship's rooms below deck share (ADR 0033, lifted from
 * brad-airship `airship-prefabs.js` and `airship-portals.js`): both rooms sit
 * against the ship's side, so they share the hull's rhythm (frames between
 * portlights at the exterior's lower-deck light spacing), the portlight glass
 * that lets the time of day's daylight in, the portlights themselves, the
 * companionway ladder up to the deck hatch, and the flush hatch drawn at an
 * interior portal. Changed from the prototype: the kit's `box` and `tag`
 * replace `addBlock` and `tagGeometry` (same arguments), the glass is
 * finished with `kit.finish` (the cutaway and, below deck, the radiance
 * volume), and the hatch reads the kit's materials by key.
 */
import * as THREE from 'three';
import type { AreaPortal } from './area.ts';
import { type ShipKit, box, tag } from './kit.ts';

/** Portlight spacing: the exterior's row of lower-deck lights (2.45 m); hull frames stand midway between them. */
export const PORTLIGHT_STEP = 2.45;

/** Where the hull frames stand along a wall from `start` to `end` (room-local), its two ends included. */
export const hullFrameStations = (start: number, end: number): number[] => {
  const out = [start];
  for (let u = -PORTLIGHT_STEP * 3; u <= PORTLIGHT_STEP * 3; u += PORTLIGHT_STEP) if (u > start + 0.3 && u < end - 0.3) out.push(u);
  out.push(end);
  return out;
};

/** Where the portlights stand along a wall from `start` to `end`, clear of its ends. */
export const portlightStations = (start: number, end: number): number[] => {
  const out: number[] = [];
  for (let u = -PORTLIGHT_STEP * 2.5; u <= PORTLIGHT_STEP * 2.5; u += PORTLIGHT_STEP) if (u > start + 0.6 && u < end - 0.6) out.push(u);
  return out;
};

/** The daylight glass shared by every portlight below deck (`kit.m.hullGlazing`; the time of day sets its emissive). */
export const hullGlazing = (kit: ShipKit): THREE.MeshStandardMaterial => {
  const have = kit.m['hullGlazing'];
  if (have !== undefined) return have;
  const glass = kit.finish(new THREE.MeshStandardMaterial({ color: '#a4ad9a', emissive: '#ffe2ad', emissiveIntensity: 1.05, roughness: 0.4 }));
  glass.name = 'airship-hull-glazing';
  kit.m['hullGlazing'] = glass;
  return glass;
};

/**
 * A glazed portlight in the ship's side at (x, y, z): framed glass with crossed glazing bars and a drip sill, all
 * within the wall's visual thickness (it faces +z, into the room). Cut-tagged: it opens round the traveler.
 */
export const addPortlight = (root: THREE.Object3D, kit: ShipKit, glass: THREE.Material, x: number, y: number, z: number): void => {
  const timber = kit.get('timber');
  const trim = kit.get('trim');
  const b = (mat: THREE.Material, dx: number, dy: number, dz: number, w: number, h: number, d: number): THREE.Mesh => box(root, mat, x + dx, y + dy, z + dz, w, h, d, true);
  b(glass, 0, 0, 0.01, 0.74, 0.58, 0.03);
  b(timber, 0, 0.34, 0.05, 0.94, 0.1, 0.1);
  b(timber, 0, -0.34, 0.05, 0.94, 0.1, 0.1);
  for (const dx of [-0.42, 0.42]) b(timber, dx, 0, 0.05, 0.1, 0.78, 0.1);
  b(trim, 0, 0, 0.04, 0.74, 0.045, 0.04);
  b(trim, 0, 0, 0.04, 0.045, 0.58, 0.04);
  b(trim, 0, -0.42, 0.08, 1.02, 0.06, 0.16);
};

/**
 * The steep ship's ladder from the landing grating up to the companionway hatch in the deck overhead (its foot at
 * (x, z − 0.75)); it stops at `topY`, the cut plane where the walls stop.
 */
export const addCompanionLadder = (root: THREE.Object3D, kit: ShipKit, x: number, z: number, topY = 2.9): void => {
  const timber = kit.get('timber');
  const floor = kit.get('floor');
  const foot = z - 0.75;
  const top = z + 0.55;
  const run = top - foot;
  const length = Math.hypot(run, topY);
  const tilt = Math.atan2(run, topY);
  for (const dx of [-0.4, 0.4]) {
    const s = box(root, timber, x + dx, topY / 2, (foot + top) / 2, 0.07, length, 0.2, true);
    s.rotation.x = tilt;
  }
  const steps = 10;
  for (let k = 1; k <= steps; k += 1) {
    const t = k / (steps + 1);
    box(root, floor, x, t * topY, foot + t * run, 0.78, 0.045, 0.17, true);
  }
};

/**
 * The flush hatch at an interior portal (`airship-portals.js`): a framed trap of alternating boards with hinges, a
 * ring pull and a little painted plate. Presentation only: the scene record owns the portal and the walking surface.
 */
export const buildPortalHatch = (parent: THREE.Object3D, kit: ShipKit, portal: AreaPortal): void => {
  const { x, z } = portal;
  const b = (material: string, dx: number, y: number, dz: number, w: number, h: number, d: number): THREE.Mesh => box(parent, kit.get(material), x + dx, y, z + dz, w, h, d);
  b('trim', 0, 0.014, 0, 1.32, 0.028, 1.32);
  for (let i = 0; i < 5; i += 1) b(i % 2 === 1 ? 'timber' : 'floor', 0, 0.038, (i - 2) * 0.239, 1.19, 0.026, 0.224);
  for (const dx of [-0.43, 0.43]) {
    b('trim', dx, 0.058, 0, 0.075, 0.02, 1.19);
    for (const dz of [-0.47, 0.47]) b('warm', dx, 0.072, dz, 0.04, 0.008, 0.04);
  }
  const ring = new THREE.Mesh(tag(new THREE.TorusGeometry(0.095, 0.021, 4, 10)), kit.get('warm'));
  ring.rotation.x = Math.PI / 2;
  ring.position.set(x, 0.083, z + 0.3);
  parent.add(ring);
  b('blue', 0, 0.068, -0.4, 0.3, 0.014, 0.14);
  for (const dx of [-0.065, 0, 0.065]) b('cream', dx, 0.079, -0.4, 0.026, 0.008, 0.065);
};
